import React, { useEffect, useState, useRef } from 'react';
import { useParams, useHistory } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faArrowUp,
  faDownload,
  faSync,
} from '@fortawesome/free-solid-svg-icons';

import _ from 'lodash';
import meta from '../../config/meta.js';
import env from '../../config/env.js';

import useHttpStatus from '~/commons/hooks/useHttpStatus';
import PageHttp from '~/commons/components/PageHttp.jsx';
import UnloadPrompt from '../../../commons/components/UnloadPrompt.jsx';

import FormAnswer from '../forms/FormAnswer.jsx';
import Padding from '../layout/Padding.jsx';
import Article from '../blocks/Article.jsx';
import ExtraReading from '../blocks/ExtraReading.jsx';
import KeyLearning from '../blocks/KeyLearning.jsx';
import NavMobileAssignment from '../blocks/NavMobileAssignment.jsx';
import ModalAnsRes from '../modals/ModalAnsRes.jsx';
import BtnExtraReadRemind from '../btns/BtnExtraReadRemind.jsx';
import useQuery from '../../../commons/hooks/useQuery.js';

const ArticleTitleBox = ({
  className,
  slot = 1,
  article,
  onGoBack,
  scrollRef,
  showBack = false,
  showDownload = false,
  onDownload,
  showRefresh = false,
  onRefresh,
}) => {
  const [showBackToTop, setShowBackToTop] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      if (scrollRef?.current) {
        const { scrollTop } = scrollRef.current;
        setShowBackToTop(scrollTop > 300); // Show button after scrolling 300px
      }
    };

    const scrollElement = scrollRef?.current;
    if (scrollElement) {
      scrollElement.addEventListener('scroll', handleScroll);
      return () => scrollElement.removeEventListener('scroll', handleScroll);
    }
    return undefined;
  }, [scrollRef]);

  const scrollToTop = () => {
    if (scrollRef?.current) {
      scrollRef.current.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  return (
    <div className={className}>
      {/* First row with subject icon and reading material */}
      <div className="row align-items-center mb-3 g-1">
        {/* Subject-specific SVG icon - leftmost */}
        <div className="col article-subjects">
          {(article.subjects || []).map((subject, i) => (
            <img key={i} src={`/assets/img/${subject}.svg`} alt={subject} />
          ))}
        </div>
        <div className="col-auto text-center article-material-title">
          <img className="left" src="/assets/img/planet.png" />
          閱讀材料{slot}
          <img className="right" src="/assets/img/rocket.png" />
        </div>
        {/* Most right: Back button and Download button */}
        <div
          className="col article-btn-back d-flex justify-content-end gap-2"
          style={{ gap: '16px' }}
        >
          {showRefresh && (
            <button
              className="btn btn-link d-flex align-items-center"
              onClick={onRefresh}
              title="重新載入文章"
              style={{ gap: '6px' }}
            >
              <FontAwesomeIcon icon={faSync} className="me-2" />
              <span className="d-none d-sm-inline">刷新</span>
            </button>
          )}
          {showDownload && (
            <button
              className="btn btn-link d-flex align-items-center"
              onClick={onDownload}
              title="下載文章及題目"
              style={{ gap: '6px' }}
            >
              <FontAwesomeIcon icon={faDownload} className="me-2" />
              <span className="d-none d-sm-inline">下載</span>
            </button>
          )}
          {showBack && (
            <button
              className="btn btn-link d-flex align-items-center"
              onClick={onGoBack}
            >
              <img src="/assets/img/back.svg" alt="返回主頁" />
              <span className="d-none d-sm-inline">返回主頁</span>
              <span className="d-inline d-sm-none">返回</span>
            </button>
          )}
        </div>
      </div>
      {/* Second row with title centered under reading material */}
      <div className="row">
        <div className="col text-center">
          <div className="article-title">{article.title || ''}</div>
        </div>
      </div>

      {/* Back to top button - floating */}
      {showBackToTop && (
        <button
          className="btn btn-primary position-fixed d-flex align-items-center justify-content-center"
          style={{
            bottom: '20px',
            right: '20px',
            width: '50px',
            height: '50px',
            borderRadius: '50%',
            zIndex: 1000,
            backgroundColor: '#FF831E',
            border: 'none',
            boxShadow: '0 2px 10px rgba(0,0,0,0.2)',
          }}
          onClick={scrollToTop}
          aria-label="回到頂部"
        >
          <FontAwesomeIcon
            icon={faArrowUp}
            style={{
              fontSize: '20px',
              color: 'white',
            }}
          />
        </button>
      )}
    </div>
  );
};

const PageReadingMission = () => {
  const { _id } = useParams();
  const { review } = useQuery();
  const history = useHistory();
  const [re, setRe] = useState(undefined);
  const [res, req] = useHttpStatus();
  const [resModal, setResModal] = useState(false);
  const [activePane, setActivePane] = useState('article');
  const [shouldPromptBeforeLeave, setShouldPromptBeforeLeave] = useState(false);
  const [articleRefreshKey, setArticleRefreshKey] = useState(0);
  const { reviewOpenDate, reviewCloseDate } = env.certSetting;
  const rOpenMs = reviewOpenDate ? Date.parse(reviewOpenDate) : NaN;
  const rCloseMs = reviewCloseDate ? Date.parse(reviewCloseDate) : NaN;
  const now = Date.now();
  const withinGlobalReview = now >= rOpenMs && now <= rCloseMs;
  const scrollRef = useRef();

  const extraRead = (score) => {
    setRe({
      ...(re || {}),
      answer: { ...re.answer, score, extraReading: true },
    });
  };

  const handleGoBack = () => {
    if (shouldPromptBeforeLeave) {
      // Show confirmation dialog manually for the button click
      // eslint-disable-next-line no-alert
      const shouldLeave = window.confirm(
        '仍未提交答案，離開頁面視為放棄作答。不能重答，確認要離開？',
      );

      if (shouldLeave) {
        // Same logic as onBeforeLeave
        // setShouldPromptBeforeLeave(false);
        // setIsLocallyCompleted(true);
        // sessionStorage.setItem(`reading-mission-completed-${_id}`, 'true');
        history.goBack();
      }
    } else {
      history.goBack();
    }
  };

  const handleDownloadPDF = () => {
    // Get current user info from env
    const { user } = env;

    // Construct PDF download URL with user's answer ID
    const params = new URLSearchParams({
      answerId: _id,
      userId: user._id,
    });

    // Open PDF download in new window
    window.open(`/api/quiz/answers/pdf?${params.toString()}`, '_blank');
  };

  const handleRefreshArticle = () => {
    setArticleRefreshKey((k) => k + 1);
  };

  useEffect(() => {
    // Update the answer to answering status and fill the startAt.
    req({
      method: 'patch',
      url: `/api/quiz/answers/${_id}`,
      data: {
        status: 'start',
      },
    });
  }, [_id]);

  // Update prompt state based on answer status
  useEffect(() => {
    const doc = res.payload || {};

    // Switch to article pane on mobile after submission if completed
    if (doc.status === 'completed') {
      setActivePane('article');
    }

    // const shouldPrompt = doc.status === 'started' && !isLocallyCompleted;
    // setShouldPromptBeforeLeave(shouldPrompt);
  }, [res?.payload?.status, _id]);
  return (
    <PageHttp res={res}>
      {(doc) => {
        // console.log('doc', doc);
        const { article } = doc;

        // Add safety checks for article
        if (!article) {
          return <div>Loading...</div>;
        }

        const { extraReading, keyLearning } = article;
        // doc is the Answer document itself
        // doc.answers is the array of user responses to questions
        // doc.article.questions is the array of question objects
        const answerDocument = doc; // The full Answer document
        let { questions } = doc.article;

        meta.set({ title: article.title });
        const currentMission = env.assignments.find(
          (a) => a._id === doc.assignment,
        );
        const currentStatus = re && re.status ? re.status : doc.status;
        const isDisbaleAnswer = currentStatus === 'completed' || currentMission.closed;

        // Check if all periods have passed (all assignments are closed)
        const allPeriodsPassed = env.assignments.every(
          (a) => a.closed === true,
        );

        let answer = answerDocument; // Use the full Answer document

        if (re) {
          if (re.answer) answer = re.answer;
          if (re.questions) questions = re.questions;
        }
        return (
          <>
            {/* <UnloadPrompt
              when={shouldPromptBeforeLeave}
              message="仍未提交答案，離開頁面視為放棄作答。不能重答，確認要離開？"
              onBeforeLeave={async () => {
                const currentDoc = res.payload || {};
                if (currentDoc.status === 'started') {
                  // eslint-disable-next-line no-console

                  // Update local state after API call
                  setShouldPromptBeforeLeave(false);
                  setIsLocallyCompleted(true);
                  sessionStorage.setItem(`reading-mission-completed-${_id}`, 'true');
                  history.goBack();
                }
              }}
            /> */}
            <header className="mission-header container-fluid p-0 overflow-hidden d-flex position-relative justify-content-center align-items-center -min-vh-30">
              <div className="missions-title-box">
                {currentMission ? currentMission.title : ''}閱讀任務
              </div>
            </header>
            <div className="position-relative">
              {/* modal after submit */}
              <ModalAnsRes
                answer={answer}
                extraReading={extraReading}
                keyLearning={keyLearning}
                show={resModal}
                onHide={() => setResModal(false)}
                onExtraRead={extraRead}
              />
              {!isDisbaleAnswer && (
                <NavMobileAssignment
                  active={activePane}
                  onChange={setActivePane}
                />
              )}
              <div className="container-fluid">
                <div className="row">
                  <div
                    className={`
                    col-12 col-md p-0 shadow-r scrollable
                    ${!isDisbaleAnswer && activePane !== 'article' ? 'd-none d-md-block' : ''}
                  `}
                    style={{ zIndex: 2 }}
                    ref={scrollRef}
                  >
                    {/* <Padding className="pos-sticky bg-light py-3 d-none d-md-block">
                    <span className="mr-3">閱讀文章</span>
                    {moment(doc.date).format('YYYY年M月D日')}
                    <img
                      className="mx-2"
                      src={`/assets/img/${
                        answer
                          ? (moment(doc.date).isoWeekday(7).endOf('d')
                              .isAfter(moment(answer.createdAt))
                              ? 'read-green.svg' : 'read.svg')
                          : 'unfinished.svg'
                      }`}
                      style={{ width: '20px' }}
                    />
                  </Padding> */}
                    <Padding
                      className="pos-sticky bg-white py-3 d-none d-md-block"
                      style={{ zIndex: 20 }}
                    >
                      <ArticleTitleBox
                        className="article-title-bar"
                        article={article}
                        slot={doc.slot}
                        onGoBack={() => handleGoBack()}
                        showBack={isDisbaleAnswer}
                        showDownload={withinGlobalReview}
                        showRefresh
                        onRefresh={handleRefreshArticle}
                        // showDownload
                        onDownload={handleDownloadPDF}
                        scrollRef={scrollRef}
                      />
                    </Padding>
                    <Padding className="py-4 article-content">
                      <ArticleTitleBox
                        className="pb-3 d-block d-md-none"
                        article={article}
                        slot={doc.slot}
                        onGoBack={() => handleGoBack()}
                        showBack={isDisbaleAnswer}
                        showDownload={withinGlobalReview}
                        showRefresh
                        onRefresh={handleRefreshArticle}
                        onDownload={handleDownloadPDF}
                        scrollRef={scrollRef}
                      />
                      <Article
                        key={`article-${articleRefreshKey}`}
                        article={article}
                        hideTags
                        hideTitle
                      />
                      {extraReading && extraReading.url && answer && (
                        <BtnExtraReadRemind
                          onClick={() => {
                            const s = scrollRef.current;

                            s.scrollTo(0, s.scrollHeight);
                          }}
                        />
                      )}
                    </Padding>
                    {answer && (
                      <Padding className="py-4 bg-lighter">
                        <div className="readable">
                          {keyLearning && (
                            <KeyLearning>{keyLearning}</KeyLearning>
                          )}
                          {extraReading && extraReading.url && (
                            <ExtraReading
                              assignment={doc}
                              onExtraRead={extraRead}
                              extraReading={extraReading}
                              answer={answer}
                            />
                          )}
                        </div>
                      </Padding>
                    )}
                  </div>
                  {!isDisbaleAnswer && (
                    <div
                      className={`
                    col-12 col-md col-lg-auto p-0 bg-paper scrollable
                    ${activePane !== 'answer' ? 'd-none d-md-block' : ''}
                  `}
                      style={{ width: '460px' }}
                    >
                      <FormAnswer
                        id={doc._id}
                        questions={questions}
                        answer={answerDocument}
                        status={doc.status}
                        assignment={doc.assignment}
                        autoSave={!currentMission.closed}
                        onSubmitted={(p) => {
                          // Update both the answer data and status immediately
                          setRe({
                            ...p,
                            status: 'completed', // Set the completed status locally
                          });
                          setResModal(true);
                          // No need for additional API call - status is updated locally
                        }}
                        disableShowCorrectAnswers={!currentMission.closed}
                        disableTotalMarks={_.some(
                          env.assignments,
                          (a) => a.opened === true,
                        )}
                      />
                    </div>
                  )}
                </div>
              </div>
            </div>
          </>
        );
      }}
    </PageHttp>
  );
};

export default PageReadingMission;
